import React, { useState, useEffect, useCallback } from "react";
import { getUser, uploadImage, userName as updateUserName, userGrade as updateUserGrade } from "../services/UserService";
import { getGrades } from "../services/coursesService";
// @ts-ignore
import ApiNames from "../constants/ApiNames";
import { useNavigate } from "react-router-dom";
// @ts-ignore
import { NavigateNullMsisdn, getDataFromCookies } from "../utils/CommonFunction";
import EditProfile, { UserDetails, Grade } from "../components/EditProfile";
import EditProfileSkeleton from "../components/EditProfileSkeleton";

export interface EditProfileScreenProps {
  token?: string;
  loaderStatus: (status: boolean) => void;
}

const EditProfileScreen: React.FC<EditProfileScreenProps> = (props) => {
  const { cookieMsisdn } = getDataFromCookies();
  const navigate = useNavigate();

  const [details, setDetails] = useState<UserDetails | null>(null);
  const [encodedImage, setEncodedImage] = useState<string | ArrayBuffer | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [grade, setGrade] = useState<Grade[]>([]);
  const [userGrade, setUserGrade] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [title] = useState<string>("Profile");

  const getDetails = useCallback(async () => {
    console.warn("getDetails", props.token);
    const msisdn = cookieMsisdn;
    try {
      setIsLoading(true);
      const res = await getUser(msisdn, props.token);
      if (res && res.response) {
        setDetails(res.response);
      }
    } catch (error) {
      console.warn("Error fetching user details", error);
    } finally {
      setIsLoading(false);
      props.loaderStatus(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookieMsisdn, props.token]);

  const encodeImageFileAsURL = useCallback((element: HTMLInputElement) => {
    const file = element.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = function () {
        setEncodedImage(reader.result);
        setDetails((prev) => ({ ...prev, img_path: reader.result as string }));
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const getLevels = useCallback(async () => {
    try {
      const data = await getGrades(props.token);
      if (data && data.response) {
        setGrade(data.response.map((item: any) => ({ ...item, id: String(item.id) })));
      }
    } catch (error) {
      console.warn("Error fetching grades", error);
    }
  }, [props.token]);

  // for fetching user details when page loads
  useEffect(() => {
    let isActive = true;
    if (isActive && props.token !== null) {
      getDetails();
    } else if (props.token === null) {
      setIsLoading(false);
      props.loaderStatus(true);
    }
    return () => {
      isActive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token, getDetails]);

  // for changing user grade
  useEffect(() => {
    NavigateNullMsisdn();
    document.title = title;
    if (cookieMsisdn === null) {
      navigate("/home");
    } else if (props.token !== null) {
      getLevels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token, getLevels, title, cookieMsisdn, navigate]);

  const setDp = useCallback(async () => {
    const formdata = new FormData();
    const number = cookieMsisdn;
    formdata.append("msisdn", number);
    if (encodedImage) {
      formdata.append("imgUrI", encodedImage as Blob | string);
    }
    await uploadImage(formdata, props.token);
  }, [cookieMsisdn, encodedImage, props.token]);

  const submit = useCallback(async () => {
    try {
      if (userName) {
        const number = cookieMsisdn;
        await updateUserName(number, userName, props.token);
      }
      if (userGrade) {
        const number = cookieMsisdn;
        await updateUserGrade(number, userGrade, props.token);
      }
      if (encodedImage) {
        await setDp();
      }
      window.location.reload();
    } catch (error) {
      console.warn("Error during submit profile update", error);
    }
  }, [userName, cookieMsisdn, userGrade, encodedImage, props.token, setDp]);

  const onChangeUserName = useCallback((name: string) => {
    setUserName(name);
  }, []);

  const onChangeGrade = useCallback((gradeId: string) => {
    setUserGrade(gradeId);
  }, []);

  if (isLoading) {
    return <EditProfileSkeleton />;
  }

  return (
    <EditProfile
      isLoading={false}
      details={details}
      grade={grade}
      userGrade={userGrade}
      userName={userName}
      onChangeUserName={onChangeUserName}
      onChangeGrade={onChangeGrade}
      onUploadImage={encodeImageFileAsURL}
      onSubmit={submit}
    />
  );
};

export default EditProfileScreen;
